import React from 'react';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '../../../shared/ButtonLoadingIcon.tsx';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import {
  INTERNAL_APPROVAL_ADDITIONAL_DESCRIPTION_TEXT,
  UDBTF_APPROVAL_LETTER_LOADING_TEXT,
} from './constants.ts';
import type { TInternalApprovalLetterConfig } from './types.ts';

type TInternalApprovalLetterDocumentSectionProps = {
  additionalDescription: string;
  approvalConfig: TInternalApprovalLetterConfig;
  conclusionPdfUrl: string;
  isDocumentLoading: boolean;
  isConclusionDocumentLoading: boolean;
  isConclusionGenerating: boolean;
  isPageLoading: boolean;
  isSignDisabled: boolean;
  isSigningLoading: boolean;
  isStatusUpdating: boolean;
  pdfUrl: string;
  onAdditionalDescriptionChange: (value: string) => void;
  onAdditionalDescriptionConfirm: () => void;
  onOpenConclusionPdf: () => void;
  onOpenPdf: () => void;
  onRevoke: () => void;
  onSignAndConfirm: () => void;
};

const InternalApprovalLetterDocumentSection: React.FC<
  TInternalApprovalLetterDocumentSectionProps
> = ({
  additionalDescription,
  approvalConfig,
  conclusionPdfUrl,
  isDocumentLoading,
  isConclusionDocumentLoading,
  isConclusionGenerating,
  isPageLoading,
  isSignDisabled,
  isSigningLoading,
  isStatusUpdating,
  pdfUrl,
  onAdditionalDescriptionChange,
  onAdditionalDescriptionConfirm,
  onOpenConclusionPdf,
  onOpenPdf,
  onRevoke,
  onSignAndConfirm,
}) => {
  const { texts } = approvalConfig;
  const conclusionDocument = approvalConfig.conclusionDocument;
  const isSignAndConfirmDisabled =
    isDocumentLoading ||
    isConclusionGenerating ||
    isPageLoading ||
    isStatusUpdating ||
    isSignDisabled;

  return (
    <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
      <FlowSection
        step={texts.sectionStep}
        title={texts.sectionTitle}
        description={texts.sectionDescription}
      >
        <Box sx={guaranteesClientPageStyles.generatedDocumentPreview}>
          {isDocumentLoading ? (
            <Box sx={guaranteesClientPageStyles.flowLoadingState}>
              <CircularProgress size={24} />
              {UDBTF_APPROVAL_LETTER_LOADING_TEXT}
            </Box>
          ) : (
            <Box
              component="iframe"
              src={pdfUrl || undefined}
              title={texts.previewTitle}
              sx={guaranteesClientPageStyles.generatedDocumentIframe}
            />
          )}

          <Box sx={guaranteesClientPageStyles.generatedDocumentActions}>
            <Button
              variant="outlined"
              color="primary"
              type="button"
              sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
              disabled={!pdfUrl}
              onClick={onOpenPdf}
            >
              {texts.openButton}
            </Button>
          </Box>
        </Box>
      </FlowSection>

      {approvalConfig.hasAdditionalDescription && (
        <FlowSection
          step="Опис"
          title={INTERNAL_APPROVAL_ADDITIONAL_DESCRIPTION_TEXT.label}
          description=""
        >
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={INTERNAL_APPROVAL_ADDITIONAL_DESCRIPTION_TEXT.label}
            placeholder={INTERNAL_APPROVAL_ADDITIONAL_DESCRIPTION_TEXT.placeholder}
            value={additionalDescription}
            onChange={(event) => onAdditionalDescriptionChange(event.target.value)}
            slotProps={{
              htmlInput: {
                maxLength: INTERNAL_APPROVAL_ADDITIONAL_DESCRIPTION_TEXT.maxLength,
              },
            }}
          />

          <Box sx={guaranteesClientPageStyles.flowActions}>
            <Button
              variant="contained"
              color="primary"
              type="button"
              sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
              disabled={isConclusionGenerating}
              onClick={onAdditionalDescriptionConfirm}
              startIcon={isConclusionGenerating ? <ButtonLoadingIcon /> : undefined}
            >
              {INTERNAL_APPROVAL_ADDITIONAL_DESCRIPTION_TEXT.confirmButton}
            </Button>
          </Box>
        </FlowSection>
      )}

      {conclusionDocument && (
        <FlowSection
          step={conclusionDocument.sectionStep}
          title={conclusionDocument.sectionTitle}
          description={conclusionDocument.sectionDescription}
        >
          <Box sx={guaranteesClientPageStyles.generatedDocumentPreview}>
            {isConclusionDocumentLoading ? (
              <Box sx={guaranteesClientPageStyles.flowLoadingState}>
                <CircularProgress size={24} />
                {UDBTF_APPROVAL_LETTER_LOADING_TEXT}
              </Box>
            ) : (
              <Box
                component="iframe"
                src={conclusionPdfUrl || undefined}
                title={conclusionDocument.previewTitle}
                sx={guaranteesClientPageStyles.generatedDocumentIframe}
              />
            )}

            <Box sx={guaranteesClientPageStyles.generatedDocumentActions}>
              <Button
                variant="outlined"
                color="primary"
                type="button"
                sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
                disabled={!conclusionPdfUrl}
                onClick={onOpenConclusionPdf}
              >
                {texts.openButton}
              </Button>
            </Box>
          </Box>
        </FlowSection>
      )}

      <Box sx={guaranteesClientPageStyles.flowPageActionsCentered}>
        <Button
          variant="outlined"
          color="primary"
          type="button"
          sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
          disabled={isStatusUpdating}
          onClick={onRevoke}
        >
          {texts.revokeButton}
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="button"
          sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
          disabled={isSignAndConfirmDisabled}
          onClick={onSignAndConfirm}
          startIcon={isSigningLoading || isStatusUpdating ? <ButtonLoadingIcon /> : undefined}
        >
          {texts.signAndConfirmButton}
        </Button>
      </Box>
    </Box>
  );
};

export default InternalApprovalLetterDocumentSection;
